import React, { useContext, useEffect, useState } from "react";
import { AppContext } from "../context/AppContext";
import Modal from "./Modal";
import SwitcherNoText from "./buttons/SwitcherNoText";
import InputWithLabel from "./inputs/InputWithLabel";
import styles from "../css/ModalUserSettings.module.css";
import updateUser from "../utils/axios_functions/updateUser";
import icons from "../img/icons.svg";
import axiosInstance from "../api/AxiosConfig";

const EyeIcon = () => (
  <svg className="icon__svg">
    <use
      xlinkHref={`${icons}#eye`}
      x="0"
      y="0"
      width="100%"
      height="100%"
    ></use>
  </svg>
);

const ModalUserSettings = ({ label }) => {
  const { 
    userData, 
    setShowAccountSettingsModal, 
    setIsUserDataLoading,
    showAlert,
    setUserData
  } = useContext(AppContext);
  
  const [isDfaChecked, setIsDfaChecked] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  // Stavy pro hesla
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");

  // Stavy pro zobrazení/skrývání hesla u každého inputu
  const [oldPasswordVisible, setOldPasswordVisible] = useState(false);
  const [newPasswordVisible, setNewPasswordVisible] = useState(false);
  const [confirmNewPasswordVisible, setConfirmNewPasswordVisible] = useState(false);

  // Validace nového hesla (kontrolované v useEffect)
  const [isPasswordLengthValid, setIsPasswordLengthValid] = useState(false);
  const [isPasswordLettersValid, setIsPasswordLettersValid] = useState(false);
  const [isPasswordNumberValid, setIsPasswordNumberValid] = useState(false);
  const [doPasswordsMatch, setDoPasswordsMatch] = useState(false);

  // Stav pro chybové hlášky u jednotlivých inputů
  const [errorOldPassword, setErrorOldPassword] = useState("");
  const [errorNewPassword, setErrorNewPassword] = useState("");
  const [errorConfirmNewPassword, setErrorConfirmNewPassword] = useState("");

  useEffect(() => {
    if (Object.keys(userData).length > 0) {
      setIsDfaChecked(userData?.dfa);
    }
  }, [userData]);

  useEffect(() => {
    setIsPasswordLengthValid(newPassword.length >= 8);
    setIsPasswordLettersValid(
      /[a-z]/.test(newPassword) && /[A-Z]/.test(newPassword)
    );
    setIsPasswordNumberValid(/\d/.test(newPassword));
    setDoPasswordsMatch(
      newPassword !== "" &&
        confirmNewPassword !== "" &&
        newPassword === confirmNewPassword
    );
  }, [newPassword, confirmNewPassword]);

  // Při změně inputu vymažeme chybovou zprávu pro dané pole
  const handleOldPasswordChange = (e) => {
    setOldPassword(e.target.value);
    setErrorOldPassword("");
  };

  const handleNewPasswordChange = (e) => {
    setNewPassword(e.target.value);
    setErrorNewPassword("");
  };

  const handleConfirmNewPasswordChange = (e) => {
    setConfirmNewPassword(e.target.value);
    setErrorConfirmNewPassword("");
  };

  const toggleOldPasswordVisibility = () =>
    setOldPasswordVisible(!oldPasswordVisible);
  const toggleNewPasswordVisibility = () =>
    setNewPasswordVisible(!newPasswordVisible);
  const toggleConfirmNewPasswordVisibility = () =>
    setConfirmNewPasswordVisible(!confirmNewPasswordVisible);

  const handleConfirm = async () => {
    const updatedData = { dfa: isDfaChecked };
    if (
      newPassword &&
      isPasswordLengthValid &&
      isPasswordLettersValid &&
      isPasswordNumberValid &&
      doPasswordsMatch
    ) {
      // Předáme nové heslo
      updatedData.password = newPassword;
    }
    const response = await updateUser(updatedData, setIsUserDataLoading);
    if (response.status === 200) {
      // Po úspěšném update načteme čerstvá data
      const detailResponse = await axiosInstance.get("user/detail/");
      // Aktualizujeme kontext, předpokládáme, že máte setUserData v AppContext
      setUserData(detailResponse.data);
      showAlert("Nastavení účtu bylo úspěšně aktualizováno.", "info");
    } else {
      showAlert("Nastala chyba při aktualizaci účtu.", "danger");
    }
    setShowAccountSettingsModal(false);
    setShowConfirm(false);
  };
  

  const checkOrContinue = async () => {
    // Pokud není zadáno nové heslo, není potřeba kontrolovat staré – prostě pokračujeme
    if (!newPassword) {
      setShowConfirm(true);
      return;
    }
    // Ověření nového hesla – ukázka chybových zpráv pod inputy
    if (!oldPassword) {
      setErrorOldPassword("Pro změnu hesla musíte zadat své aktuální heslo.");
      return;
    }
    if (!isPasswordLengthValid) {
      setErrorNewPassword("Nové heslo musí mít alespoň 8 znaků.");
      return;
    }
    if (!isPasswordLettersValid) {
      setErrorNewPassword("Nové heslo musí obsahovat velká i malá písmena.");
      return;
    }
    if (!isPasswordNumberValid) {
      setErrorNewPassword("Nové heslo musí obsahovat číslo.");
      return;
    }
    if (!doPasswordsMatch) {
      setErrorConfirmNewPassword("Hesla se neshodují.");
      return;
    }
    try {
      // Odeslání requestu na ověření starého hesla
      const response = await axiosInstance.post(
        "user/password_check/",
        { password: oldPassword }
      );
      // Předpokládáme, že API vrací { valid: true } pokud je heslo správné
      if (response.data.valid) {
        setShowConfirm(true);
      } else {
        setErrorOldPassword("Aktuální heslo není správné.");
      }
    } catch (error) {
      console.error("Chyba při ověřování hesla:", error);
      setErrorOldPassword("Došlo k chybě při ověřování hesla. Zkuste to prosím znovu.");
    }
  };

  return (
    <>
      <Modal
        title="Nastavení účtu"
        cancelText="Zavřít"
        confirmText="Uložit"
        onConfirm={checkOrContinue}
        onClose={() => setShowAccountSettingsModal(false)}
      >
        <section className={styles.twoFactorContainer}>
          <label>{label}</label>
          <SwitcherNoText
            value={isDfaChecked}
            activeText="Aktivní"
            inactiveText="Neaktivní"
            isChecked={isDfaChecked}
            setIsChecked={setIsDfaChecked}
          />
        </section>
        <section className={styles.newPContainer}>
          <hr />
          <h4>Nové heslo</h4>
          <article className={styles.passInputBox}>
            <InputWithLabel
              id="oldP"
              label="Aktuální heslo:"
              type={oldPasswordVisible ? "text" : "password"}
              value={oldPassword}
              onChange={handleOldPasswordChange}
              autoComplete="current-password"
              icon={<EyeIcon />}
              onIconClick={toggleOldPasswordVisibility}
              iconColor={oldPasswordVisible ? "#22e06c" : "#BFBBBB"}
              error={errorOldPassword}
            />
            <InputWithLabel
              id="newP"
              label="Nové heslo:"
              type={newPasswordVisible ? "text" : "password"}
              value={newPassword}
              onChange={handleNewPasswordChange}
              autoComplete="new-password"
              icon={<EyeIcon />}
              onIconClick={toggleNewPasswordVisibility}
              iconColor={newPasswordVisible ? "#22e06c" : "#BFBBBB"}
              error={errorNewPassword}
            />
            <InputWithLabel
              id="newPConfirm"
              label="Potvrďte nové heslo:"
              type={confirmNewPasswordVisible ? "text" : "password"}
              value={confirmNewPassword}
              onChange={handleConfirmNewPasswordChange}
              autoComplete="new-password"
              icon={<EyeIcon />}
              onIconClick={toggleConfirmNewPasswordVisibility}
              iconColor={confirmNewPasswordVisible ? "#22e06c" : "#BFBBBB"}
              error={errorConfirmNewPassword}
            />
          </article>
          <article className={styles.passIndicators}>
            <ul className="form__check">
              <li className={isPasswordLengthValid ? "is-checked" : ""}>
                Minimálně 8 znaků
              </li>
              <li className={isPasswordLettersValid ? "is-checked" : ""}>
                Velká a malá písmena
              </li>
              <li className={isPasswordNumberValid ? "is-checked" : ""}>
                Obsahuje číslo
              </li>
              <li className={doPasswordsMatch ? "is-checked" : ""}>
                Hesla se shodují
              </li>
            </ul>
          </article>
        </section>
      </Modal>
      {showConfirm && (
        <Modal
          bgOpacity={1}
          onConfirm={handleConfirm}
          onClose={() => setShowConfirm(false)}
        >
          <p>Opravdu chcete provést změnu?</p>
        </Modal>
      )}
    </>
  );
};

export default ModalUserSettings;
